<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h6 class="m-0 font-weight-bold text-muted">
        <i class="fa fa-list mr-1"></i> Listagem de Volumes
      </h6>
      <div>
        <b-button variant="primary" class="rounded-pill px-3 shadow-sm" @click="onAdd" :disabled="!canEdit || loading">
          <i class="fa fa-plus mr-1"></i> Incluir Volume
        </b-button>
        <b-button variant="outline-warning" class="rounded-pill px-3 ml-2 shadow-sm" :disabled="!canEdit || !selected || loading" @click="onEdit">
          <i class="fa fa-edit mr-1"></i> Alterar
        </b-button>
        <b-button variant="outline-danger" class="rounded-pill px-3 ml-2 shadow-sm" :disabled="!canEdit || !selected || loading" @click="onDelete">
          <i class="fa fa-trash mr-1"></i> Excluir
        </b-button>
      </div>
    </div>

    <b-overlay :show="loading" opacity="0.6" rounded="sm">
      <b-table 
        class="premium-table"
        :items="volumes" 
        :fields="fields"
        selectable 
        @row-selected="onRowSelected" 
        select-mode="single" 
        responsive
        hover
        show-empty
        empty-text="Nenhum volume cadastrado."
      >
        <template #cell(NR_QUANTIDADE)="data">
          <span class="font-weight-bold">{{ data.item.NR_QUANTIDADE || 0 }}</span>
        </template>
        <template #cell(TX_ESPECIE)="data">{{ data.item.TX_ESPECIE || '-' }}</template>
        <template #cell(TX_MARCA)="data">{{ data.item.TX_MARCA || '-' }}</template>
        <template #cell(TX_NUMERACAO)="data">{{ data.item.TX_NUMERACAO || '-' }}</template>
        <template #cell(NR_PESO_LIQUIDO)="data">
          <span class="text-info">{{ formatNumber(data.item.NR_PESO_LIQUIDO) }}</span>
        </template>
        <template #cell(NR_PESO_BRUTO)="data">
          <span class="text-primary font-weight-bold">{{ formatNumber(data.item.NR_PESO_BRUTO) }}</span>
        </template>
      </b-table>
    </b-overlay>

    <NfeTranspVolumeModal 
      ref="modal" 
      modalId="modal-volume-nfe"
      :volume="volumeEditando"
      :nfeId="nfeId"
      @salvar="onSalvarVolume" 
    />
  </div>
</template>

<script>
import NfeTranspVolumeModal from './NfeTranspVolumeModal.vue'
import Tools from '@/tools/tools.js'

export default {
  name: 'NfeTranspVolumes',
  components: { NfeTranspVolumeModal },
  props: {
    nfeId: { type: Number, default: null },
    canEdit: { type: Boolean, default: true }
  },
  data() {
    return {
      volumes: [],
      loading: false,
      selected: null,
      volumeEditando: null,
      fields: [
        { key: 'NR_QUANTIDADE', label: 'Quantidade', thStyle: { width: '12%' } },
        { key: 'TX_ESPECIE', label: 'Espécie', thStyle: { width: '18%' } },
        { key: 'TX_MARCA', label: 'Marca', thStyle: { width: '18%' } },
        { key: 'TX_NUMERACAO', label: 'Numeração', thStyle: { width: '18%' } },
        { key: 'NR_PESO_LIQUIDO', label: 'Peso Líquido', class: 'text-right', thStyle: { width: '17%' } },
        { key: 'NR_PESO_BRUTO', label: 'Peso Bruto', class: 'text-right', thStyle: { width: '17%' } }
      ]
    }
  },
  async mounted() {
    await this.carregarVolumes()
  },
  watch: {
    nfeId: {
      handler(newVal) {
        if (newVal) {
          this.carregarVolumes()
        }
      }
    }
  },
  methods: {
    async carregarVolumes() {
      if (!this.nfeId) return
      
      try {
        this.loading = true
        const resp = await this.$http.get(`/api/protected/notaFiscal/volume/getByNfeId/${this.nfeId}`)
        this.volumes = resp.data || []
      } catch (err) {
        Tools.exibirMensagem(this.$store, 'Aviso', 'danger', err)
      } finally {
        this.loading = false
      }
    },

    onAdd() {
      if (!this.canEdit) {
        this.toast('Edição não permitida para o status atual da NF-e', 'warning')
        return
      }
      
      this.volumeEditando = null
      this.selected = null
      this.$refs.modal.mostrar()
    },

    async onEdit() {
      if (!this.selected) return
      
      if (!this.canEdit) {
        this.toast('Edição não permitida para o status atual da NF-e', 'warning')
        return
      }

      try {
        this.loading = true
        
        // Buscar dados completos do volume
        const resp = await this.$http.get(`/api/protected/notaFiscal/volume/getById/${this.selected.ID}`)
        const data = resp.data
        
        // Backend pode retornar array, pegar primeiro elemento
        this.volumeEditando = Array.isArray(data) ? data[0] : data
        
        // Abrir modal
        this.$nextTick(() => {
          this.$refs.modal.mostrar()
        })
        
      } catch (err) {
        Tools.exibirMensagem(this.$store, 'Aviso', 'danger', err)
      } finally {
        this.loading = false
      }
    },

    async onDelete() {
      if (!this.selected) return
      
      if (!this.canEdit) {
        this.toast('Edição não permitida para o status atual da NF-e', 'warning')
        return
      }

      const confirmado = await this.$bvModal.msgBoxConfirm(
        'Deseja realmente excluir este volume?',
        {
          title: 'Confirmar Exclusão',
          okVariant: 'danger',
          okTitle: 'Sim',
          cancelTitle: 'Não'
        }
      )
      
      if (!confirmado) return

      try {
        this.loading = true
        
        await this.$http.post('/api/protected/notaFiscal/volume/delete', {
          volumeId: this.selected.ID
        })

        this.toast('Volume excluído com sucesso', 'success')
        
        // Recarregar lista
        await this.carregarVolumes()
        
        this.selected = null
        this.$emit('changed')
        
      } catch (err) {
        Tools.exibirMensagem(this.$store, 'Aviso', 'danger', err)
      } finally {
        this.loading = false
      }
    },

    onRowSelected(items) {
      this.selected = items && items.length ? items[0] : null
    },

    async onSalvarVolume(volume) {
      try {
        this.loading = true
        
        const endpoint = volume.ID 
          ? '/api/protected/notaFiscal/volume/edit'
          : '/api/protected/notaFiscal/volume/post'
        
        await this.$http.post(endpoint, volume)
        
        this.toast(
          volume.ID ? 'Volume atualizado com sucesso' : 'Volume adicionado com sucesso',
          'success'
        )
        
        // Recarregar lista
        await this.carregarVolumes()
        
        this.selected = null
        this.volumeEditando = null
        this.$emit('changed')
        
      } catch (err) {
        Tools.exibirMensagem(this.$store, 'Aviso', 'danger', err)
        throw err // Repassar erro para o modal não fechar
      } finally {
        this.loading = false
      }
    },

    formatNumber(v) {
      return Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },

    toast(msg, variant = 'info') {
      this.$bvToast && this.$bvToast.toast(msg, {
        title: 'Volumes',
        variant,
        autoHideDelay: 3500,
        solid: true
      })
    }
  }
}
</script>

<style scoped>
</style>



// WEBPACK FOOTER //
// src/views/faturamento/nfe/partials/NfeTranspVolumes.vue